import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Command } from "cmdk";
import {
  Search, LayoutDashboard, Users, ShieldCheck, Settings, Activity,
  CalendarClock, MessageSquare, Columns3, ClipboardList, UserCog, Wallet, BookOpen,
} from "lucide-react";
import { useCompany } from "@/context/CompanyContext";
import { api } from "@/lib/api";

const MODULE_ICONS = {
  bookings: CalendarClock, comms: MessageSquare, boards: Columns3, rotas: ClipboardList,
  hr: UserCog, pay: Wallet, books: BookOpen,
};

/**
 * Cmd+K palette with an extensible provider registry.
 * Providers are shape: (ctx) => [{ id, section, label, hint, onSelect, icon }]
 * Modules can register their own providers later via `registerCommandProvider`.
 */
const _providers = new Set();

export function registerCommandProvider(fn) { _providers.add(fn); return () => _providers.delete(fn); }

export default function CommandPalette({ open, onOpenChange }) {
  const nav = useNavigate();
  const { companyId } = useParams();
  const { catalog, entitlements, has } = useCompany();
  const [q, setQ] = React.useState("");
  const [members, setMembers] = React.useState([]);

  // Keyboard shortcut
  React.useEffect(() => {
    const on = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [open, onOpenChange]);

  React.useEffect(() => { if (!open) setQ(""); }, [open]);

  // Debounced member search
  React.useEffect(() => {
    if (!open || !companyId) return () => {};
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/search/members`, { params: { q } });
        setMembers(data.members || []);
      } catch { setMembers([]); }
    }, 120);
    return () => clearTimeout(h);
  }, [q, open, companyId]);

  const modules = catalog?.modules || [];
  const enabled = entitlements?.modules || {};

  function jump(path) { onOpenChange(false); nav(path); }

  const navCommands = [
    { id: "nav-dashboard", section: "Navigate", label: "Dashboard", icon: LayoutDashboard, onSelect: () => jump(`/c/${companyId}`) },
    { id: "nav-members",   section: "Navigate", label: "Members",   icon: Users,           onSelect: () => jump(`/c/${companyId}/members`) },
    ...(has("roles.manage") ? [{ id: "nav-roles", section: "Navigate", label: "Roles", icon: ShieldCheck, onSelect: () => jump(`/c/${companyId}/roles`) }] : []),
    ...(has("audit.view")   ? [{ id: "nav-audit", section: "Navigate", label: "Audit",   icon: Activity,   onSelect: () => jump(`/c/${companyId}/audit`) }] : []),
    { id: "nav-settings",  section: "Navigate", label: "Settings",  icon: Settings,        onSelect: () => jump(`/c/${companyId}/settings`) },
  ];

  const moduleCommands = modules
    .filter((m) => enabled[m.id] && has(m.view_capability))
    .map((m) => ({
      id: `mod-${m.id}`,
      section: "Modules",
      label: m.label,
      icon: MODULE_ICONS[m.id] || Columns3,
      hint: m.tagline,
      onSelect: () => jump(`/c/${companyId}/m/${m.id}`),
    }));

  // Registered command providers (extensibility)
  const externalCommands = [];
  for (const provider of _providers) {
    try {
      const items = provider({ companyId, has, entitlements }) || [];
      externalCommands.push(...items);
    } catch {/* provider error, ignore */}
  }

  if (!open) return null;

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
      onClick={() => onOpenChange(false)}
      data-testid="command-palette"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg bg-card shadow-card border border-brand-rule overflow-hidden"
      >
        <Command shouldFilter={true} filter={(value, search) => {
          if (!search) return 1;
          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
        }}>
          <div className="flex items-center gap-2 px-3.5 py-3 border-b border-brand-rule">
            <Search size={14} className="text-brand-faint" />
            <Command.Input
              autoFocus
              value={q}
              onValueChange={setQ}
              placeholder="Jump to a page, module, or member…"
              className="flex-1 outline-none bg-transparent text-[13px] placeholder:text-brand-faint"
              data-testid="palette-input"
            />
            <span className="mono-num text-[10px] chip">esc</span>
          </div>
          <Command.List className="max-h-[420px] overflow-y-auto py-1.5">
            <Command.Empty className="px-4 py-8 text-[12.5px] text-brand-muted text-center"
                           data-testid="palette-empty">
              No matches for "{q}"
            </Command.Empty>
            <PaletteGroup heading="Navigate" items={navCommands} testidPrefix="palette-nav" />
            <PaletteGroup heading="Modules" items={moduleCommands} testidPrefix="palette-module" />
            {externalCommands.length > 0 && (
              <PaletteGroup heading="Actions" items={externalCommands} testidPrefix="palette-ext" />
            )}
            {members.length > 0 && (
              <Command.Group heading="Members" className="px-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:micro-label">
                {members.map((m) => (
                  <Command.Item
                    key={`member-${m.id}`}
                    value={`member ${m.name} ${m.email}`}
                    onSelect={() => { onOpenChange(false); nav(`/c/${companyId}/members?open=${m.id}`); }}
                    data-testid={`palette-member-${m.id}`}
                    className="flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-[12.5px] data-[selected=true]:bg-accent"
                  >
                    <span className="h-5 w-5 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center">
                      {(m.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="font-medium text-brand-ink">{m.name}</span>
                    <span className="text-brand-muted truncate">{m.email}</span>
                    <span className="ml-auto text-[10px] mono-num chip">member</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}

function PaletteGroup({ heading, items, testidPrefix }) {
  if (!items.length) return null;
  return (
    <Command.Group heading={heading} className="px-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:micro-label">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <Command.Item
            key={it.id}
            value={`${heading} ${it.label} ${it.hint || ""}`}
            onSelect={it.onSelect}
            data-testid={`${testidPrefix}-${it.id}`}
            className="flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-[12.5px] data-[selected=true]:bg-accent"
          >
            {Icon && <Icon size={13} className="text-brand-violet" />}
            <span className="font-medium text-brand-ink">{it.label}</span>
            {it.hint && <span className="text-brand-muted text-[11px] truncate">— {it.hint}</span>}
          </Command.Item>
        );
      })}
    </Command.Group>
  );
}
